import { TextField, Menu, Box, Typography, InputAdornment, MenuItem, CircularProgress, Chip } from "@mui/material";
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import useDebounce from "../hooks/useDebounce";
import Iconify from "../components/Iconify";

// Main component for the custom select dropdown
export const CustomMultiSelect = ({
    label,
    value = [], // Default to an empty array for multi-select
    onChange,
    fetchFunction,
    required,
    backendSearchable,
    valueField = 'value',
    labelField = 'label',
    disabled,
    error,
    helperText,
    handleBlur = () => { },
    className,
    placeholder,
    option,
}) => {

    const [options, setOptions] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [inputValue, setInputValue] = useState('');
    const debouncedInputValue = useDebounce(inputValue, 300);
    const limit = 10;

    const dropdownRef = useRef(null);
    const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
    const dropdownOpen = Boolean(dropdownAnchorEl);
    const loadMoreRef = useRef(null);

    // Function to load options from the backend
    const loadOptions = useCallback(async (currentOffset, searchTerm) => {
        if (option?.length > 0) return; // Skip fetching if options are provided via props
        try {
            setLoading(true);
            const newOptions = await fetchFunction(currentOffset, limit, searchTerm);
            const safeNewOptions = Array.isArray(newOptions) ? newOptions : [];

            setOptions(prevOptions => {
                const safePrevOptions = Array.isArray(prevOptions) ? prevOptions : [];
                // Reset options if offset is 0 (first load or search)
                return currentOffset === 0 ? safeNewOptions : [...safePrevOptions, ...safeNewOptions];
            });

            setHasMore(safeNewOptions.length === limit);
        } catch (error) {
            console.error('Error fetching options:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction, option]);

    // Handle opening the dropdown menu
    const handleDropdownMenuClick = useCallback((event) => {
        setDropdownAnchorEl(event.currentTarget);
        setOffset(0); // Reset offset when opening dropdown
        loadOptions(0, "");
    }, [loadOptions]);

    // Handle closing the dropdown menu
    const handleDropdownMenuClose = useCallback(() => {
        setDropdownAnchorEl(null);
        setOffset(0); // Reset offset when closing
        // Do not clear options if `option` contains data
        if (!option?.length) {
            setOptions([]);
        }
    }, [option]);

    useEffect(() => {
        setOptions(option?.length > 0 ? option : []);
    }, [option]);

    // Use Intersection Observer to load more options when the "load more" element is in view
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                const nextOffset = offset + limit;
                loadOptions(nextOffset, inputValue);
                setOffset(nextOffset);
            }
        }, {
            root: dropdownRef.current,
            rootMargin: '0px',
            threshold: 1.0
        });

        const loadMoreElement = loadMoreRef.current;
        if (loadMoreElement) {
            observer.observe(loadMoreElement);
        }

        return () => {
            if (loadMoreElement) {
                observer.unobserve(loadMoreElement);
            }
        };
    }, [hasMore, loading, offset, loadOptions, inputValue]);

    // Handle changes in the search input
    const handleSearchChange = useCallback((newInputValue) => {
        setInputValue(newInputValue);
        if (backendSearchable) {
            // Reset options and page then fetch with new term
            setOptions([]);
            setOffset(0);
            loadOptions(0, newInputValue);
        }
    }, [backendSearchable, loadOptions]);

    // Handle removing a selected value
    const handleRemove = useCallback((removedValue) => {
        const updatedValues = value.filter(v => v[valueField] !== removedValue[valueField]);
        onChange(updatedValues);
    }, [onChange, value, valueField]);

    // Handle selecting an option from the dropdown
    const handleSelectOption = useCallback((newValue) => {
        if (!newValue) return;
        const alreadySelected = value.some(v => v[valueField] === newValue[valueField]);
        if (!alreadySelected) {
            onChange([...value, newValue]); // Add the new value to the array
        }
        setInputValue(""); // Reset search value
    }, [onChange, value, valueField]);

    // Filter options on the frontend when backendSearchable is false
    const filteredOptions = useMemo(() => {
        // If using backend search, options are already filtered from the server.
        if (backendSearchable) {
            return options.filter(option => !value.some(v => v[valueField] === option[valueField]));
        }
        // For frontend filtering, filter by search term and also exclude selected values.
        const search = debouncedInputValue.toLowerCase();
        return options?.filter(option => {
            const labelText = String(option[labelField]).toLowerCase();
            const matchesSearch = labelText.includes(search);
            const notSelected = !value.some(v => v[valueField] === option[valueField]);
            return matchesSearch && notSelected;
        });
    }, [options, debouncedInputValue, backendSearchable, value, valueField, labelField]);

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.5 : 1,
        }}>
            <TextField
                id={!value.length ? "" : ":r7:"}
                placeholder={!value.length ? placeholder : ''}
                label={label}
                fullWidth
                required={required}
                InputProps={{
                    readOnly: true,
                    startAdornment: (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                alignItems: 'center',
                                maxHeight: '100%',
                            }}
                        >
                            {value.map((selectedValue, index) => (
                                <Chip
                                    key={index}
                                    label={selectedValue[labelField]}
                                    onDelete={() => handleRemove(selectedValue)}
                                />
                            ))}
                        </Box>
                    ),
                    style: {
                        display: 'flex',
                        flexWrap: 'wrap',
                        minHeight: 'fit-content',
                        paddingRight: '32px',
                    },
                    endAdornment: (
                        <Iconify
                            icon={
                                dropdownOpen
                                    ? 'iconamoon:arrow-down-2-light'
                                    : 'iconamoon:arrow-right-2-light'
                            }
                            onClick={handleDropdownMenuClick}
                            style={{
                                cursor: 'pointer',
                                position: 'absolute',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                            }}
                        />
                    ),
                }}
                onClick={handleDropdownMenuClick}
                onBlur={handleBlur}
                helperText={helperText}
                error={error}
                className={className}
                multiline
            />


            <Menu
                ref={dropdownRef}
                anchorEl={dropdownAnchorEl}
                open={dropdownOpen}
                onClose={handleDropdownMenuClose}
                PaperProps={{
                    style: {
                        width: dropdownAnchorEl ? dropdownAnchorEl.getBoundingClientRect().width : 'auto',
                        marginTop: '5px',
                        padding: '0px 5px'
                    },
                }}>
                <TextField
                    placeholder='Chercher'
                    value={inputValue}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Iconify icon='ic:round-search' />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            inputValue && (
                                <InputAdornment position="end">
                                    <Iconify
                                        icon='ic:round-close'
                                        onClick={() => handleSearchChange('')}
                                        sx={{ cursor: 'pointer', fontSize: '20px' }}
                                    />
                                </InputAdornment>
                            )
                        ),
                    }}
                />
                <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                    {filteredOptions?.length > 0 ? (
                        filteredOptions?.map((option, index) => (
                            <MenuItem
                                key={index}
                                onClick={() => handleSelectOption(option)}
                            >
                                {option[labelField]}
                            </MenuItem>
                        ))
                    ) : (
                        !loading && <MenuItem disabled>Aucun résultat trouvé</MenuItem>
                    )}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                    <Typography ref={loadMoreRef} />
                </Box>
            </Menu>
        </Box>
    );
};